import React, { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { CONFETTI_LOGO_INTERVAL_MS } from '../../config/decoracion';
import './LogoMundial.css';

// Paleta celeste y blanco (bandera argentina)
const COLORS = ['#75AADB', '#4A90D9', '#A8D0EF', '#FFFFFF', '#FFFFFF'];
const PUB = process.env.PUBLIC_URL;

// Sprites generados desde el video que_ese_sea_el_hincha_que_e.mp4
// (_assets_originales/make_sprites_v2.py adaptado). El recorrido completo es
// natural: mira -> gol -> corre a la izquierda -> GIRO real (se ve la espalda)
// -> corre a la derecha -> llega. Las duraciones = nº de cuadros x 42ms (24fps).
const SPRITES = {
  mira: 'hincha2_idle.png',      // estático: mira el partido (espejado, mira al tele)
  gol: 'hincha2_gol.webp',       // festeja en su lugar
  ida: 'hincha2_ida.webp',       // corre de perfil hacia la izquierda
  giro: 'hincha2_giro.webp',     // se da vuelta (rotación real, muestra la espalda)
  vuelta: 'hincha2_vuelta.webp', // corre de perfil hacia la derecha (vuelve)
  llega: 'hincha2_llega.webp',   // se acomoda en su lugar
};
const GOL_MS = 966;
const IDA_MS = 1974;
const GIRO_MS = 714;
const VUELTA_MS = 1554;
const LLEGA_MS = 798;

// Posición horizontal por fase. 'home' = su lugar (derecha, junto al tele).
// 'left' = la punta izquierda (junto al isologo), donde gira.
// El movimiento se hace con transition de CSS, con la duración de cada fase.
const HOME = 'var(--hincha-home)';
const LEFT = 'var(--hincha-left)';
const POS = {
  mira:   { right: HOME, ms: 0, ease: 'linear' },
  gol:    { right: HOME, ms: 0, ease: 'linear' },
  // ida: frena al llegar al giro (ease-out) así no "patina" antes de girar
  ida:    { right: LEFT, ms: IDA_MS, ease: 'ease-out' },
  giro:   { right: LEFT, ms: 0, ease: 'linear' },   // gira en el lugar
  // vuelta: arranca despacio al salir del giro y frena al llegar a casa
  vuelta: { right: HOME, ms: VUELTA_MS, ease: 'ease-in-out' },
  llega:  { right: HOME, ms: 0, ease: 'linear' },
};

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Televisor visto DESDE ATRÁS (lo que mira el hincha).
const TvSVG = () => (
  <svg className="tv-svg" viewBox="0 0 70 60" aria-hidden="true">
    <line x1="35" y1="16" x2="24" y2="3" stroke="#555" strokeWidth="2" strokeLinecap="round" />
    <line x1="35" y1="16" x2="48" y2="4" stroke="#555" strokeWidth="2" strokeLinecap="round" />
    <rect className="tv-glow" x="4" y="12" width="62" height="44" rx="8" fill="#cdeed4" />
    <rect x="9" y="15" width="52" height="38" rx="6" fill="#4a4a4a" stroke="#333" strokeWidth="1" />
    <g stroke="#5e5e5e" strokeWidth="2" strokeLinecap="round">
      <line x1="15" y1="22" x2="30" y2="22" />
      <line x1="15" y1="27" x2="30" y2="27" />
      <line x1="15" y1="32" x2="30" y2="32" />
    </g>
    <rect x="38" y="23" width="16" height="17" rx="3" fill="#3d3d3d" />
    <path d="M42 53 q 5 7 0 12 t 5 9" fill="none" stroke="#333" strokeWidth="1.6" strokeLinecap="round" />
    <rect x="17" y="53" width="5" height="5" rx="1" fill="#333" />
    <rect x="48" y="53" width="5" height="5" rx="1" fill="#333" />
  </svg>
);

// Hincha al lado del logo: mira el partido y cuando hay gol explota, sale
// corriendo a la izquierda, gira (rotación real) y vuelve a su lugar.
// Fases: mira -> gol -> ida -> giro -> vuelta -> llega -> mira.
const LogoMundial = () => {
  const ref = useRef(null);
  const [fase, setFase] = useState('mira');
  const lock = useRef(false);
  const timers = useRef([]);

  const fireConfetti = () => {
    const el = ref.current;
    if (!el || document.hidden) return;
    const r = el.getBoundingClientRect();
    if (!r.width) return;
    const x = (r.left + r.width / 2) / window.innerWidth;
    const y = (r.top + r.height / 2) / window.innerHeight;
    const common = {
      origin: { x, y },
      colors: COLORS,
      zIndex: 99999,
      disableForReducedMotion: true,
      ticks: 460,
      gravity: 1.05,
    };
    confetti({ ...common, particleCount: 140, spread: 120, startVelocity: 62, angle: 75, drift: 1.2 });
    confetti({ ...common, particleCount: 90, spread: 165, startVelocity: 50, angle: 90, drift: 0.6 });
  };

  const celebrate = () => {
    if (lock.current || prefersReduced || document.hidden) return;
    lock.current = true;
    const t = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    setFase('gol');
    t(fireConfetti, 500);
    t(() => setFase('ida'), GOL_MS);
    t(() => setFase('giro'), GOL_MS + IDA_MS);
    t(fireConfetti, GOL_MS + IDA_MS + GIRO_MS); // un confeti al volver
    t(() => setFase('vuelta'), GOL_MS + IDA_MS + GIRO_MS);
    // sin fase "llega" (quedaba mirando al lado contrario): al terminar la
    // vuelta queda directo en idle, mirando al tele.
    t(() => {
      setFase('mira');
      lock.current = false;
    }, GOL_MS + IDA_MS + GIRO_MS + VUELTA_MS);
  };

  // Cada 2 minutos (CONFETTI_LOGO_INTERVAL_MS) festeja solo
  useEffect(() => {
    if (prefersReduced) return undefined;
    Object.values(SPRITES).forEach((s) => {
      fetch(`${PUB}/img/${s}`).catch(() => {});
    });
    const id = setInterval(celebrate, CONFETTI_LOGO_INTERVAL_MS);
    return () => {
      clearInterval(id);
      timers.current.forEach(clearTimeout);
    };
  }, []);

  const src = prefersReduced ? 'hincha2_idle.png' : SPRITES[fase];
  const pos = POS[fase] || POS.mira;

  return (
    <>
      <span className="tv-logo" aria-hidden="true">
        <TvSVG />
      </span>
      <span
        className="hincha-logo"
        ref={ref}
        aria-hidden="true"
        onMouseEnter={celebrate}
        style={{
          right: pos.right,
          transition: pos.ms ? `right ${pos.ms}ms ${pos.ease}` : 'none',
        }}
      >
        <img src={`${PUB}/img/${src}`} alt="" className={`is-${fase}`} />
      </span>
    </>
  );
};

export default LogoMundial;
